import React, { useMemo } from 'react';
import { Card, CardBody, Media } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import currencyFormatter from '../../../../utils/currencyFormatter';
import { monthRefDefault } from './utils';
import { useReceitasResumo } from './useReceitasResumo';

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const pct = (value, target) => {
  const v = Number(value) || 0;
  const t = Number(target) || 0;
  if (t <= 0) return 0;
  return (v / t) * 100;
};

const SummaryCard = ({ title, value, sub, onClick, selected }) => {
  return (
    <div
      style={{ width: '100%', height: '100%' }}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && typeof onClick === 'function') onClick();
      }}
    >
      <Card
        style={{
          cursor: 'pointer',
          height: '100%',
          border: selected ? '2px solid #4e515e' : '2px solid transparent',
          boxShadow: selected ? '0 6px 20px rgba(0,0,0,0.10)' : undefined,
        }}
      >
        <CardBody style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Media>
            <Media body className="overflow-hidden">
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontWeight: 800 }} className="text-truncate font-size-14 mb-2">
                  {title}
                </span>
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, lineHeight: 1.1 }}>{value}</div>
              {sub && <div style={{ fontSize: 12, color: '#6c757d', marginTop: 6 }}>{sub}</div>}
            </Media>
          </Media>
          <div style={{ flex: 1 }} />
        </CardBody>
      </Card>
    </div>
  );
};

const GaugeCard = ({ title, value, target, footerLeft, footerRight, onClick, selected, invert = false, validationTag = false }) => {
  const raw = pct(value, target);
  const percent = clamp(raw, 0, 120); // permite "passar de 100%" sem estourar layout
  const effective = invert ? 100 - clamp(raw, 0, 100) : clamp(raw, 0, 100);

  const color =
    invert
      ? effective >= 70
        ? '#1cbb8c'
        : effective >= 40
          ? '#f1b44c'
          : '#ff5757'
      : effective >= 70
        ? '#1cbb8c'
        : effective >= 40
          ? '#f1b44c'
          : '#ff5757';

  return (
    <div
      style={{ width: '100%', height: '100%' }}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && typeof onClick === 'function') onClick();
      }}
    >
      <Card
        style={{
          cursor: 'pointer',
          height: '100%',
          border: selected ? '2px solid #4e515e' : '2px solid transparent',
          boxShadow: selected ? '0 6px 20px rgba(0,0,0,0.10)' : undefined,
        }}
      >
        <CardBody style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center" style={{ gap: 8, minWidth: 0 }}>
              <span style={{ fontWeight: 800 }} className="text-truncate font-size-14 mb-2">
                {title}
              </span>
              {validationTag && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    padding: '2px 7px',
                    borderRadius: 999,
                    color: '#7a5600',
                    backgroundColor: 'rgba(241,180,76,0.18)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Dados em validação
                </span>
              )}
            </div>
            <span style={{ fontSize: 12, color: '#6c757d', fontWeight: 700 }}>
              {Number.isFinite(raw) ? `${Math.round(raw)}%` : '0%'}
            </span>
          </div>

          <ReactApexChart
            type="radialBar"
            height={190}
            series={[percent]}
            options={{
              chart: { sparkline: { enabled: true } },
              plotOptions: {
                radialBar: {
                  startAngle: -135,
                  endAngle: 135,
                  hollow: { size: '60%' },
                  track: { background: '#eef2f7' },
                  dataLabels: {
                    name: { show: false },
                    value: {
                      show: true,
                      fontSize: '22px',
                      fontWeight: 900,
                      offsetY: 8,
                      formatter: (v) => `${Math.round(v)}%`,
                    },
                  },
                },
              },
              fill: { colors: [color] },
              stroke: { lineCap: 'round' },
              labels: [title],
            }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#6c757d' }}>
            <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{footerLeft}</div>
            <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{footerRight}</div>
          </div>

          {/* espaço reservado para o usuário final (observações/ações) */}
          <div style={{ minHeight: 28 }} />
        </CardBody>
      </Card>
    </div>
  );
};

export default function ExecutiveSummary({
  recs,
  mesRef,
  setMesRef,
  activeExecutiveKey,
  onSelectExecutive,
}) {
  const resumo = useReceitasResumo({ recs, mesRef });

  const barComparativo = useMemo(() => {
    const categories = [
      'Faturamento previsto (bruto)',
      'Faturamento realizado (bruto)',
      'Receita líquida prevista',
      'Receita líquida realizada',
      'Em atraso (líquido)',
    ];
    const values = [
      resumo.fatPrevMesBru,
      resumo.fatRealMesBru,
      resumo.recLiqPrevMes,
      resumo.recLiqRealMes,
      resumo.inadimplenciaLiq,
    ];
    const keyByIndex = ['fat_prev', 'fat_real', 'liq_prev', 'liq_real', 'inadimplencia'];

    return {
      categories,
      values,
      keyByIndex,
      series: [{ name: 'Valor', data: values }],
    };
  }, [resumo]);

  const donutFaturamento = useMemo(() => {
    return {
      series: [resumo.fatDistFaturadoBru, resumo.fatDistLiberadoBru, resumo.fatDistBloqueadoBru],
      labels: ['Faturado', 'Liberado', 'Bloqueado'],
      keyByIndex: ['fat_real', 'fat_prev', 'fat_prev'],
    };
  }, [resumo]);

  const donutRecebimentos = useMemo(() => {
    return {
      series: [resumo.recDistRecebidoLiq, resumo.recDistAbertoNoPrazoLiq, resumo.recDistAtrasoLiq],
      labels: ['Recebido', 'Em aberto no prazo', 'Em atraso'],
      keyByIndex: ['liq_real', 'liq_prev', 'inadimplencia'],
    };
  }, [resumo]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="d-flex justify-content-between align-items-end" style={{ gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 18, fontWeight: 900 }}>Resumo executivo</div>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <div style={{ fontSize: 12, color: '#6c757d' }}>Mês:</div>
          <input
            type="month"
            value={mesRef || monthRefDefault()}
            onChange={(e) => setMesRef(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ced4da' }}
          />
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setMesRef(monthRefDefault())}>
            Mês atual
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
          alignItems: 'stretch',
        }}
      >
        <GaugeCard
          title="Faturamento (realizado vs previsto)"
          value={resumo.fatRealMesBru}
          target={resumo.fatPrevMesBru}
          footerLeft={`Realizado: ${currencyFormatter.format(resumo.fatRealMesBru)} (${resumo.fatRealCount} itens)`}
          footerRight={`Previsto: ${currencyFormatter.format(resumo.fatPrevMesBru)} (${resumo.fatPrevCount} itens)`}
          selected={activeExecutiveKey === 'fat_real' || activeExecutiveKey === 'fat_prev'}
          onClick={() => typeof onSelectExecutive === 'function' && onSelectExecutive('fat_real')}
        />
        <GaugeCard
          title="Receita líquida (realizada vs prevista)"
          value={resumo.recLiqRealMes}
          target={resumo.recLiqPrevMes}
          footerLeft={`Realizada: ${currencyFormatter.format(resumo.recLiqRealMes)} (${resumo.recLiqRealCount} itens)`}
          footerRight={`Prevista: ${currencyFormatter.format(resumo.recLiqPrevMes)} (${resumo.recLiqPrevCount} itens)`}
          selected={activeExecutiveKey === 'liq_real' || activeExecutiveKey === 'liq_prev'}
          validationTag
          onClick={() => typeof onSelectExecutive === 'function' && onSelectExecutive('liq_real')}
        />
        <GaugeCard
          title="Em atraso (quanto menor, melhor)"
          value={resumo.inadimplenciaLiq}
          target={resumo.emAbertoNoMesLiq || resumo.recLiqPrevMes || 1}
          invert
          footerLeft={`${resumo.inadimplenciaCount} títulos em atraso`}
          footerRight={`Em aberto no mês: ${currencyFormatter.format(resumo.emAbertoNoMesLiq)}`}
          selected={activeExecutiveKey === 'inadimplencia'}
          validationTag
          onClick={() => typeof onSelectExecutive === 'function' && onSelectExecutive('inadimplencia')}
        />
      </div>

      <div className="d-flex" style={{ gap: 12 }}>
        <Card style={{ width: '70%' }}>
          <CardBody>
            <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 6 }}>
              <div style={{ fontWeight: 800 }}>Esteira do contas a receber</div>
              <span style={{ fontSize: 11, color: '#7a5600' }}>Dados em validação</span>
            </div>
            <ReactApexChart
              type="bar"
              height={260}
              series={barComparativo.series}
              options={{
                chart: {
                  toolbar: { show: false },
                  events: {
                    dataPointSelection: (_e, _ctx, config) => {
                      const idx = config?.dataPointIndex ?? -1;
                      const key = barComparativo.keyByIndex[idx];
                      if (typeof onSelectExecutive === 'function' && key) onSelectExecutive(key);
                    },
                  },
                },
                plotOptions: {
                  bar: { columnWidth: '55%', distributed: true },
                },
                stroke: { width: 0 },
                dataLabels: { enabled: false },
                xaxis: { categories: barComparativo.categories },
                yaxis: {
                  labels: {
                    formatter: (v) => currencyFormatter.format(v),
                  },
                },
                tooltip: {
                  y: { formatter: (v) => currencyFormatter.format(v) },
                },
                legend: { show: false },
              }}
            />
            <div style={{ fontSize: 12, color: '#6c757d' }}>
              Clique em uma barra para abrir o detalhe correspondente.
            </div>
          </CardBody>
        </Card>

        <Card style={{ width: '30%' }}>
          <CardBody>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Distribuição do faturamento no mês</div>
            <ReactApexChart
              type="donut"
              height={260}
              series={donutFaturamento.series}
              options={{
                chart: {
                  toolbar: { show: false },
                  events: {
                    dataPointSelection: (_e, _ctx, config) => {
                      const idx = config?.dataPointIndex ?? -1;
                      const key = donutFaturamento.keyByIndex[idx];
                      if (typeof onSelectExecutive === 'function' && key) onSelectExecutive(key);
                    },
                  },
                },
                labels: donutFaturamento.labels,
                dataLabels: { enabled: false },
                legend: { position: 'bottom' },
                tooltip: {
                  y: { formatter: (v) => currencyFormatter.format(v) },
                },
              }}
            />
          </CardBody>
        </Card>
      </div>

      <div className="d-flex" style={{ gap: 12 }}>
        <Card style={{ width: '100%' }}>
          <CardBody>
            <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 6 }}>
              <div style={{ fontWeight: 800 }}>Distribuição dos recebimentos no mês</div>
              <span style={{ fontSize: 11, color: '#7a5600' }}>Dados em validação</span>
            </div>
            <ReactApexChart
              type="donut"
              height={260}
              series={donutRecebimentos.series}
              options={{
                chart: {
                  toolbar: { show: false },
                  events: {
                    dataPointSelection: (_e, _ctx, config) => {
                      const idx = config?.dataPointIndex ?? -1;
                      const key = donutRecebimentos.keyByIndex[idx];
                      if (typeof onSelectExecutive === 'function' && key) onSelectExecutive(key);
                    },
                  },
                },
                labels: donutRecebimentos.labels,
                dataLabels: { enabled: false },
                legend: { position: 'bottom' },
                tooltip: { y: { formatter: (v) => currencyFormatter.format(v) } },
              }}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

